// Timestamp formatting under the local-time-only convention: user-facing
// event timestamps arrive as NAIVE LOCAL wall-clock strings keyed to the
// event location ("YYYY-MM-DDTHH:MM:SS", no timezone). NEVER feed those to
// `new Date(isoString)` — the browser would parse them in the viewer's zone
// (or worse, as UTC) and shift the wall clock. We split the string into
// components and build a component-wise Date purely as a vehicle for Intl
// month/weekday names; no zone math ever happens.

const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

interface NaiveParts {
  date: Date; // local-constructed, used only for Intl labels
  hasTime: boolean;
}

function parseNaive(value: string): NaiveParts | null {
  const m = NAIVE_RE.exec(value);
  if (!m) return null;
  return {
    date: new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] ?? "0"),
      Number(m[5] ?? "0"),
      Number(m[6] ?? "0"),
    ),
    hasTime: m[4] != null,
  };
}

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
});
const DAY_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const DAY_YEAR_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });

export function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parts = parseNaive(value);
  return parts ? DATE_FMT.format(parts.date) : value;
}

export function formatTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const parts = parseNaive(value);
  if (!parts || !parts.hasTime) return null;
  return TIME_FMT.format(parts.date);
}

/** "Tue, Jun 9, 2026 · 6:00 PM – 9:00 PM" / "Jun 9 – 12, 2026" / "Jun 28 – Jul 2, 2026". */
export function formatRange(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string {
  const start = startsAt ? parseNaive(startsAt) : null;
  const end = endsAt ? parseNaive(endsAt) : null;
  if (!start) return "Dates to be confirmed";
  if (!end || sameDay(start.date, end.date)) {
    const day = DATE_FMT.format(start.date);
    if (!start.hasTime) return day;
    const from = TIME_FMT.format(start.date);
    const to = end?.hasTime && end.date.getTime() !== start.date.getTime()
      ? TIME_FMT.format(end.date)
      : null;
    return to ? `${day} · ${from} – ${to}` : `${day} · ${from}`;
  }
  const sameYear = start.date.getFullYear() === end.date.getFullYear();
  if (!sameYear) return `${DAY_YEAR_FMT.format(start.date)} – ${DAY_YEAR_FMT.format(end.date)}`;
  const sameMonth = start.date.getMonth() === end.date.getMonth();
  const left = DAY_FMT.format(start.date);
  const right = sameMonth ? String(end.date.getDate()) : DAY_FMT.format(end.date);
  return `${left} – ${right}, ${start.date.getFullYear()}`;
}

/** Session row inside a known event day: "Jun 9 · 9:00 AM – 10:30 AM". */
export function formatSessionWindow(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string {
  const start = startsAt ? parseNaive(startsAt) : null;
  if (!start) return "—";
  const day = DAY_FMT.format(start.date);
  if (!start.hasTime) return day;
  const end = endsAt ? parseNaive(endsAt) : null;
  const from = TIME_FMT.format(start.date);
  const to = end?.hasTime ? TIME_FMT.format(end.date) : null;
  return to ? `${day} · ${from} – ${to}` : `${day} · ${from}`;
}

// Audit-ish fields (sentAt / respondedAt / checkedInAt) may arrive as real
// UTC instants (trailing Z / offset) — those go through Date properly and
// render in the viewer's zone. Naive strings stay on the no-shift path.
export function formatStamp(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return `${DAY_YEAR_FMT.format(d)} · ${TIME_FMT.format(d)}`;
  }
  const parts = parseNaive(value);
  if (!parts) return value;
  return parts.hasTime
    ? `${DAY_YEAR_FMT.format(parts.date)} · ${TIME_FMT.format(parts.date)}`
    : DAY_YEAR_FMT.format(parts.date);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "draft" → "Draft", "checked-in" / "checked_in" → "Checked in". */
export function titleCase(value: string): string {
  const words = value.replace(/[-_]+/g, " ").trim();
  if (!words) return value;
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}
